import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Mail, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { admin, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (admin) return <Navigate to="/admin" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/admin');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center px-6 bg-[#020408]" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="glass rounded-2xl p-8 md:p-10 w-full max-w-md"
      >
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#00F0FF]/20 border border-[#00F0FF]/50 flex items-center justify-center cyan-glow">
            <ShieldCheck className="w-6 h-6 text-[#00F0FF]" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold">Admin Portal</h1>
            <p className="text-gray-400 text-sm">OmniBeast Services</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-300 flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#00F0FF]" /> Email
            </Label>
            <Input
              id="email"
              type="email"
              data-testid="admin-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="bg-[#0A0F14] border-[#1E293B] text-white rounded-none focus:border-[#00F0FF]"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-gray-300 flex items-center gap-2">
              <Lock className="w-4 h-4 text-[#00F0FF]" /> Password
            </Label>
            <Input
              id="password"
              type="password"
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="bg-[#0A0F14] border-[#1E293B] text-white rounded-none focus:border-[#00F0FF]"
            />
          </div>

          {error && (
            <p className="text-red-400 text-sm" data-testid="admin-login-error">{error}</p>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="w-full btn-primary rounded-none py-6 font-heading tracking-wide"
          >
            {loading ? 'SIGNING IN...' : 'SIGN IN'}
          </Button>
        </form>
      </motion.div>
    </main>
  );
}
